import type { ChangeEventHandler, FocusEventHandler, ReactNode, Ref } from "react";

import { cn } from "@/shared/lib/cn";

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  /** A small tile or icon before the label, such as a service tile or a status icon. */
  icon?: ReactNode;
}

/* Columns from `sm` up; phones get half as many (rounded up), so labels keep their room. */
const COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-4",
} as const;

const SIZE = {
  sm: "h-9 gap-2",
  md: "h-10 gap-2.5",
} as const;

interface ChoiceCardsProps<T extends string> {
  /** Names the group (rendered as its legend). */
  legend: string;
  name: string;
  options: readonly ChoiceOption<T>[];
  columns?: keyof typeof COLUMNS;
  /** Card height: "md" (h-10) for tiles, "sm" (h-9) for small icons. */
  size?: keyof typeof SIZE;
  /** Controlled use. With react-hook-form, spread `register(name)` instead. */
  value?: T;
  onValueChange?: (value: T) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  ref?: Ref<HTMLInputElement>;
  disabled?: boolean;
  className?: string;
}

/**
 * One choice out of a few, shown as cards: radio buttons underneath, so arrow keys move between
 * options and the form receives the value. The checked card is styled from the radio's own
 * state, so it works the same controlled or registered with react-hook-form.
 */
export function ChoiceCards<T extends string>({
  legend,
  name,
  options,
  columns = 2,
  size = "md",
  value,
  onValueChange,
  onChange,
  onBlur,
  ref,
  disabled,
  className,
}: ChoiceCardsProps<T>) {
  const hasDescriptions = options.some((option) => option.description);
  return (
    <fieldset disabled={disabled} className={className}>
      <legend className="mb-2 text-[14px] font-medium text-ink">{legend}</legend>
      <div className={cn("grid gap-1.5", COLUMNS[columns])}>
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex cursor-pointer rounded-lg border border-line px-2.5 text-[14px] text-ink-muted transition-colors hover:border-line-strong hover:text-ink has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-brand-ring has-[:checked]:border-brand has-[:checked]:bg-brand-soft/60 has-[:checked]:font-medium has-[:checked]:text-ink has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60",
              hasDescriptions
                ? cn(SIZE[size], "h-auto items-start py-2")
                : cn(SIZE[size], "items-center"),
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              className="sr-only"
              ref={ref}
              onBlur={onBlur}
              {...(value === undefined ? {} : { checked: option.value === value })}
              onChange={(event) => {
                onChange?.(event);
                onValueChange?.(option.value);
              }}
            />
            {option.icon}
            {option.description ? (
              <span className="flex min-w-0 flex-col">
                <span>{option.label}</span>
                <span className="text-[13px] font-normal text-ink-subtle">
                  {option.description}
                </span>
              </span>
            ) : (
              option.label
            )}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
